import { useRef, useState, useCallback } from 'react';
import { useUsers, type UserSaveData } from '@/hooks/useUsers';
import { sectors } from '@/mocks/formData';
import { onlyCpfDigits } from '@/lib/cpf';
import UserTable from './components/UserTable';
import UserModal from './components/UserModal';
import ConfirmDialog from './components/ConfirmDialog';
import type { User } from '@/types';

export default function AdminUsuarios() {
  const {
    users,
    totalUsers,
    search,
    setSearch,
    filterSetor,
    setFilterSetor,
    filterPerfil,
    setFilterPerfil,
    filterStatus,
    setFilterStatus,
    addUser,
    editUser,
    deleteUser,
    toggleStatus,
    importUsers,
  } = useUsers();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parseBoolean = (value: string) => ['sim', 's', 'true', '1', 'yes'].includes(value.trim().toLowerCase());

  const parseUsersCsv = (text: string): Array<UserSaveData & { password: string }> => {
    const lines = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length < 2) return [];

    const delimiter = lines[0].includes(';') ?';' : ',';
    const headers = lines[0].split(delimiter).map((header) => header.trim().toLowerCase());

    return lines.slice(1).map((line) => {
      const values = line.split(delimiter).map((value) => value.trim());
      const row = headers.reduce<Record<string, string>>((acc, header, index) => {
        acc[header] = values[index] || '';
        return acc;
      }, {});

      return {
        name: row.nome || row.name,
        email: row.email,
        cpf: onlyCpfDigits(row.cpf || row.matricula),
        setor: row.setor,
        perfil: (row.perfil || 'colaborador') as 'admin' | 'colaborador',
        status: (row.status || 'ativo') as 'ativo' | 'inativo',
        password: row.senha || row.password,
        forcePasswordReset: parseBoolean(row.forcar_reset_senha || row.force_password_reset || 'sim'),
      };
    }).filter((row) => row.name && row.email && row.cpf && row.setor && row.password);
  };

  const handleAdd = useCallback(() => {
    setEditingUser(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((user: User) => {
    setEditingUser(user);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setEditingUser(null);
  }, []);

  const handleSave = useCallback((data: UserSaveData) => {
    if (editingUser) {
      const payload: Partial<UserSaveData> = { ...data };
      if (!payload.password) {
        delete payload.password;
        delete payload.forcePasswordReset;
      }
      editUser(editingUser.id, payload);
    } else {
      if (!data.password) return;
      addUser({ ...data, password: data.password });
    }
  }, [editingUser, addUser, editUser]);

  const handleImportFile = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportMessage('');

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const rows = parseUsersCsv(String(reader.result || ''));
        if (rows.length === 0) {
          setImportMessage('Nenhum usuário válido encontrado na planilha.');
          return;
        }

        const result = await importUsers(rows);
        const errorText = result.errors.length ?` ${result.errors.join(' ')}` : '';
        setImportMessage(`${result.success} usuário(s) importado(s).${errorText}`);
      } catch (err) {
        setImportMessage(err instanceof Error ?err.message : 'Erro ao importar planilha.');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    reader.onerror = () => {
      setImporting(false);
      setImportMessage('Não foi possível ler o arquivo selecionado.');
    };

    reader.readAsText(file, 'utf-8');
  }, [importUsers]);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget) {
      deleteUser(deleteTarget.id);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteUser]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-2">
        <div>
          <h1 className="text-xl font-bold text-portal-gray-900">Usuários</h1>
          <p className="text-sm text-portal-gray-500">
            Gerencie os colaboradores e administradores do sistema.
            <span className="text-portal-gray-400 ml-2">({totalUsers} cadastrados)</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/assets/modelo-importacao-usuarios.csv"
            download
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-portal-purple bg-white border border-portal-purple/30 rounded-lg hover:bg-portal-purple-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line"></i>
            Baixar modelo
          </a>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleImportFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-portal-gray-700 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            <i className="ri-upload-2-line"></i>
            {importing ?'Importando...' : 'Importar planilha'}
          </button>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          Novo Usuário
        </button>
      </div>

      {importMessage && (
        <div className="mb-4 rounded-lg border border-portal-gray-200 bg-white px-4 py-3 text-sm text-portal-gray-700">
          {importMessage}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-portal-gray-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, e-mail ou CPF..."
            className="w-full text-sm pl-9 pr-4 py-2.5 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-800 placeholder-portal-gray-400 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
          />
        </div>

        <select
          value={filterSetor}
          onChange={(e) => setFilterSetor(e.target.value)}
          className="text-sm px-3 py-2.5 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
        >
          <option value="all">Todos os setores</option>
          {sectors.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={filterPerfil}
          onChange={(e) => setFilterPerfil(e.target.value)}
          className="text-sm px-3 py-2.5 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
        >
          <option value="all">Todos os perfis</option>
          <option value="admin">Administrador</option>
          <option value="colaborador">Colaborador</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-sm px-3 py-2.5 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
        >
          <option value="all">Todos os status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
        </select>
      </div>

      {/* Table */}
      <UserTable
        users={users}
        onEdit={handleEdit}
        onDelete={setDeleteTarget}
        onToggleStatus={toggleStatus}
      />

      {/* Modals */}
      <UserModal
        isOpen={modalOpen}
        editingUser={editingUser}
        onClose={handleCloseModal}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title="Excluir usuário"
        message={`Tem certeza que deseja excluir "${deleteTarget?.name}"?Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

